/**
 * SaveToWatchHistoryUseCase
 *
 * Saves the current playback position of a content item to the user's
 * watch history via the `saveToWatchHistory` cloud function.
 *
 * Flutter sources of truth:
 *   - lib/data/repository/medias_repository.dart:377-387
 *   - lib/services/cloud_functions_service.dart:1069-1090
 *   - lib/domain/notifiers/video_player_notifier.dart:309-322 (caller — pause)
 *   - lib/domain/notifiers/video_player_notifier.dart:334-354 (gating)
 *
 * Fire-and-forget contract: errors are logged and swallowed (mirrors Flutter).
 */

import { MediaService } from '@/domain/services/media/mediaService';
import { appLogger } from '@/domain/services/analytics/appLogger';

export class SaveToWatchHistoryUseCase {
  private mediaService: MediaService;

  constructor(mediaService?: MediaService) {
    this.mediaService = mediaService || new MediaService();
  }

  /**
   * Execute the use case.
   * Returns early without calling when contentId is empty.
   * Returns the success flag from the cloud function (false on any error).
   * Never throws — errors are logged and swallowed.
   */
  async execute(contentId: string, currentMillis: number, totalMillis: number): Promise<boolean> {
    if (!contentId) {
      return false;
    }

    try {
      return await this.mediaService.saveToWatchHistory(contentId, currentMillis, totalMillis);
    } catch (error) {
      appLogger.error(
        'Error executing SaveToWatchHistoryUseCase',
        'SaveToWatchHistoryUseCase', { error: error as Error },
      );
      return false;
    }
  }
}

export const saveToWatchHistoryUseCase = new SaveToWatchHistoryUseCase();
